/**
 * The AI checklist. With `onEdit`, every row can be clicked and corrected by the AM (JF 2026-09-29): free text for all
 * items, plus the structured fields the engine uses for the key ones (qty, sizes, origin, rush, compliance,
 * main fabric, complexity). An edited row shows "edited by you".
 */
import { useState } from "react";
import { checklistRows, type Item, type Items } from "@ic/engine";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";

type Field = { k: string; en: string; zh: string; type: "num" | "text" | "bool" | "select" | "city"; opts?: [string, string, string][] };
const FIELDS: Record<string, Field[]> = {
  qty: [{ k: "pcs", en: "Order qty (pcs)", zh: "订单量（件）", type: "num" }, { k: "colors", en: "Colours", zh: "颜色数", type: "num" }],
  sizes: [{ k: "count", en: "Number of sizes", zh: "码数", type: "num" }, { k: "list", en: "Sizes (comma separated)", zh: "尺码（逗号分隔）", type: "text" }, { k: "extended", en: "Extended sizes (3XL+)", zh: "含加大码（3XL 及以上）", type: "bool" }],
  origin: [{ k: "city", en: "Factory city", zh: "生产城市", type: "city" }, { k: "country", en: "Country", zh: "国家", type: "text" }],
  lead_time: [{ k: "rush", en: "Rush order", zh: "加急", type: "bool" }],
  compliance: [
    { k: "testing", en: "Testing", zh: "检测", type: "select", opts: [["none", "None", "不需要"], ["basic", "Basic", "基础"], ["full", "Full", "全套"]] },
    { k: "inspection", en: "Inspection", zh: "验货", type: "select", opts: [["none", "None", "不需要"], ["third_party", "3rd-party final", "第三方尾期"]] },
  ],
  main_fabric: [{ k: "composition", en: "Composition", zh: "成分", type: "text" }, { k: "supplier", en: "Supplier / mill", zh: "供应商 / 面料厂", type: "text" },
    { k: "source", en: "Source", zh: "来源", type: "select", opts: [["stock", "Stock", "现货"], ["custom", "Custom", "定织定染"], ["nominated", "Nominated", "指定"], ["client_supplied", "Client supplied", "客供"]] }],
  complexity: [{ k: "level", en: "Level", zh: "复杂度", type: "select", opts: [["simple", "Simple", "简单"], ["medium", "Medium", "中等"], ["complex", "Complex", "复杂"]] },
    { k: "sam_est", en: "SAM (min)", zh: "SAM（分钟）", type: "num" }],
};

export function Checklist({ items, onEdit }: { items: Items | null | undefined; onEdit?: (key: string, patch: Item) => void }) {
  const lang = useStore((s) => s.lang);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>{checklistRows(items, lang).map((r) => (
      <div key={r.key}>
        <div className={"ck" + (onEdit ? " ckedit" : "")} data-ck={r.key} onClick={onEdit ? () => setOpen(open === r.key ? null : r.key) : undefined}
          title={onEdit ? (lang === "zh" ? "点击修改" : "Click to edit") : undefined}>
          <span className="no">{r.no}</span><span className="nm">{r.name}</span><span className={"st " + r.status[1]}>{r.status[0]}</span>
          {r.value && <span className="vv">{r.value}{r.note && <> <span style={{ color: "#a39a92" }}>· {r.note}</span></>}</span>}
        </div>
        {onEdit && open === r.key && <Editor k={r.key} item={(items || {})[r.key] || {}} onCancel={() => setOpen(null)}
          onSave={(patch) => { onEdit(r.key, patch); setOpen(null); }} />}
      </div>
    ))}</>
  );
}

function Editor({ k, item, onSave, onCancel }: { k: string; item: Item; onSave(p: Item): void; onCancel(): void }) {
  const t = useT();
  const cities = useStore((s) => s.meta?.cities || []);
  const [v, setV] = useState<Item>(() => ({ ...item, list: Array.isArray(item.list) && k === "sizes" ? item.list.join(", ") : item.list }));
  const set = (key: string, val: unknown) => setV((x) => ({ ...x, [key]: val }));
  function save() {
    const out: Item = { ...v, status: "edited" };
    for (const f of FIELDS[k] || []) if (f.type === "num") out[f.k] = v[f.k] === "" || v[f.k] == null ? null : Number(v[f.k]);
    if (k === "sizes") {
      out.list = String(v.list || "").split(/[,，\s]+/).filter(Boolean);
      if (!out.count && out.list.length) out.count = out.list.length;
    }
    onSave(out);
  }
  return (
    <div className="ckform" data-ckform={k} onClick={(e) => e.stopPropagation()}>
      <div className="fl"><label>{t("Description", "描述")}</label>
        <input className="in" data-f="value" value={String(v.value ?? "")} onChange={(e) => set("value", e.target.value)} /></div>
      {(FIELDS[k] || []).map((f) => (
        <div className="fl" key={f.k}><label>{t(f.en, f.zh)}</label>
          {f.type === "bool" ? <input type="checkbox" data-f={f.k} checked={!!v[f.k]} onChange={(e) => set(f.k, e.target.checked)} />
            : f.type === "select" ? <select className="in" data-f={f.k} value={String(v[f.k] ?? "")} onChange={(e) => set(f.k, e.target.value)}>
              <option value="">—</option>{f.opts!.map(([val, en, zh]) => <option key={val} value={val}>{t(en, zh)}</option>)}</select>
            : f.type === "city" ? <select className="in" data-f={f.k} value={String(v[f.k] ?? "")} onChange={(e) => set(f.k, e.target.value)}>
              <option value="">—</option>{cities.map((c) => <option key={c}>{c}</option>)}</select>
            : <input className={"in" + (f.type === "num" ? " n" : "")} data-f={f.k} value={String(v[f.k] ?? "")} onChange={(e) => set(f.k, e.target.value)} />}
        </div>
      ))}
      <div className="mb3" style={{ marginTop: 6 }}>
        <button className="btn sm" onClick={onCancel}>{t("Cancel", "取消")}</button>
        <button className="btn sm p" data-cksave={k} onClick={save}>{t("Save", "保存")}</button>
      </div>
    </div>
  );
}
